// The screen's side of better-auth (/api/auth/*): codes by mail, passkeys,
// Google, signing out.
//
// Promise: every call returns `{ error }` instead of throwing, the way
// better-auth's client does, so a screen can always say what went wrong
// (a dropped error would leave a button that does nothing). Every request says
// the screen's language, so the code mail is written in it.
import { passkeyClient } from "@better-auth/passkey/client";
import { m } from "@trameria/cloud-i18n";
import { getLocale } from "@trameria/i18n";
import { emailOTPClient } from "better-auth/client/plugins";
import { createAuthClient } from "better-auth/react";

/** The API's limits the screen speaks of (private/apps/api/src/auth.ts). */
export const CODE_MINUTES = 10;
export const RESEND_COOLDOWN_SECONDS = 60;
export const RESEND_TOO_SOON = "RESEND_TOO_SOON";

const client = createAuthClient({
  baseURL: window.location.origin,
  plugins: [emailOTPClient(), passkeyClient()],
  fetchOptions: {
    onRequest: (ctx) => {
      ctx.headers.set("x-trameria-locale", getLocale());
      return ctx;
    },
  },
});

export interface AuthError {
  code?: string;
  message?: string;
  status?: number;
  /** Seconds until the next send (only on RESEND_TOO_SOON). */
  retryAfter?: number;
}

type Result = Promise<{ error: AuthError | null }>;

const settle = async (call: Promise<{ error: unknown }>): Result => {
  try {
    const { error } = await call;
    return { error: (error as AuthError | null) ?? null };
  } catch (cause) {
    return { error: { message: (cause as Error).message } };
  }
};

export const sendSignInCode = (email: string, captchaToken?: string): Result =>
  settle(
    client.emailOtp.sendVerificationOtp(
      { email, type: "sign-in" },
      captchaToken
        ? { headers: { "x-captcha-response": captchaToken } }
        : undefined,
    ),
  );

export const verifySignInCode = (email: string, otp: string): Result =>
  settle(client.signIn.emailOtp({ email, otp }));

/** `returnTo` is a path on this origin (better-auth redirects there after Google). */
export const signInWithGoogle = (returnTo: string): Result =>
  settle(client.signIn.social({ provider: "google", callbackURL: returnTo }));

/** Conditional UI: passkeys offered in the email field's autofill. Resolves when one is chosen. */
export const signInWithPasskeyAutofill = (): Result =>
  settle(client.signIn.passkey({ autoFill: true }));

export const signInWithPasskey = (): Result => settle(client.signIn.passkey());

export const addPasskey = (name?: string): Result =>
  settle(client.passkey.addPasskey(name ? { name } : {}));

export const linkGoogle = (returnTo: string): Result =>
  settle(client.linkSocial({ provider: "google", callbackURL: returnTo }));

export const signOut = (): Result => settle(client.signOut());

/**
 * WebAuthn folds "no passkey here" and "cancelled" into one error; the most
 * common case on the sign-in page is the first, so it says to use the email.
 */
const PASSKEY_NONE_OR_CANCELLED = new Set([
  "ERROR_PASSTHROUGH_SEE_CAUSE_PROPERTY",
  "ERROR_CEREMONY_ABORTED",
]);

export function passkeySignInErrorMessage(error: AuthError): string {
  if (error.code && PASSKEY_NONE_OR_CANCELLED.has(error.code))
    return m.web_passkey_none();
  return m.web_passkey_failed();
}

/** better-auth's codes as one line of the screen. */
export function otpErrorMessage(error: AuthError): string {
  switch (error.code) {
    // The captcha plugin's three codes (missing, refused, Cloudflare unreachable).
    case "MISSING_RESPONSE":
    case "VERIFICATION_FAILED":
    case "UNKNOWN_ERROR":
      return m.web_captcha_failed();
    case "INVALID_OTP":
      return m.web_otp_invalid();
    case "OTP_EXPIRED":
      return m.web_otp_expired();
    case "TOO_MANY_ATTEMPTS":
      return m.web_otp_too_many();
    case RESEND_TOO_SOON:
      return m.web_resend_too_soon();
    // The server's text for a ban is built once in one language.
    case "BANNED_USER":
      return m.web_banned();
  }
  // Not by status alone: a 403 is also a ban, which "try again" would not fix.
  if (error.status === 429) return m.web_rate_limited();
  if (error.status === 502) return m.web_mail_failed();
  return error.message ?? m.web_signin_failed();
}
